// Tile manager — orchestrates 648 global tiles with progressive LOD.
// Port of tile_manager.rs.

import { Cbt } from './cbt';
import { GpuCbt } from './gpu-cbt';
import { GpuInference, parseTileWeights, upsampleLod0ToHires } from './gpu-inference';
import { extractFrustumPlanes, aabbVsFrustum } from './math';
import type { Renderer } from './renderer';
import {
  type GlobalManifest, type TileSlot, type TileRenderState,
  LodLevel, HIRES_RES, MAX_LOD1_TILES, INFER_BUDGET,
  DOWNGRADE_FRAMES, UPGRADE_HOLD_FRAMES, setHiresRes,
  DETAIL_RES, DETAIL_SCALE, setDetailZoom as setDetailZoomGlobal,
} from './types';

// Max tiles to run full CBT update + mesh regen per frame.
// Distant tiles reuse cached meshes until their turn comes.
const CBT_UPDATE_BUDGET = 64;

export class TileManager {
  slots: TileSlot[];
  active: Map<number, TileRenderState>;
  private lod0Data: ArrayBuffer;
  private lod0Res: number;
  private renderer: Renderer;
  private weightsBaseUrl: string;
  private frameCounter = 0;
  private gpuInference: GpuInference | null = null;
  private gpuCbt: GpuCbt | null = null;
  gpuCbtEnabled = false;
  private fetchingSlots = new Set<number>();

  // Reusable arrays — avoid per-frame allocation
  private _visibleIdx = new Uint16Array(648);
  private _visibleDist = new Float32Array(648);
  private _visibleCount = 0;
  private _wantFocus: number[] = [];
  private _downgradeList: number[] = [];
  private _visibleBits: Uint8Array = new Uint8Array(0);

  private constructor(
    slots: TileSlot[],
    active: Map<number, TileRenderState>,
    lod0Data: ArrayBuffer,
    lod0Res: number,
    renderer: Renderer,
    weightsBaseUrl: string,
  ) {
    this.slots = slots;
    this.active = active;
    this.lod0Data = lod0Data;
    this.lod0Res = lod0Res;
    this.renderer = renderer;
    this.weightsBaseUrl = weightsBaseUrl;
  }

  static async create(
    manifest: GlobalManifest,
    lod0Blob: ArrayBuffer,
    renderer: Renderer,
    weightsBaseUrl: string,
    gpuCbtEnabled = false,
  ): Promise<TileManager> {
    const nLat = manifest.n_lat;
    const nLon = manifest.n_lon;
    const lod0Res = manifest.lod0_res;

    // Build slots
    const slots: TileSlot[] = manifest.tiles.map(t => {
      const row = (t.slot / nLon) | 0;
      const col = t.slot % nLon;
      const worldRow = nLat - 1 - row;
      return {
        tileId: t.tile_id,
        trained: t.trained,
        elevMin: t.elev_min,
        elevMax: t.elev_max,
        row,
        col,
        offsetX: col,
        offsetY: 0,
        offsetZ: worldRow,
      };
    });

    // Create all tiles at LOD0
    const active = new Map<number, TileRenderState>();
    const lod0TileBytes = lod0Res * lod0Res * 2;

    for (let i = 0; i < slots.length; i++) {
      const dataOffset = i * lod0TileBytes;
      const data = new Uint8Array(lod0Blob, dataOffset, lod0TileBytes);
      const state = createTileState(renderer, data, lod0Res, slots[i]);
      active.set(i, state);
    }

    const tm = new TileManager(slots, active, lod0Blob, lod0Res, renderer, weightsBaseUrl);
    tm.gpuCbtEnabled = gpuCbtEnabled;

    // Init inference pipeline
    try {
      const inferCode = await fetch('shaders/infer.wgsl').then(r => r.text());
      tm.gpuInference = new GpuInference(renderer.device, inferCode);
    } catch (e) {
      console.warn('GPU inference not available:', e);
    }

    // Init GPU CBT pipeline
    if (gpuCbtEnabled) {
      try {
        const cbtCode = await fetch('shaders/cbt_compute.wgsl').then(r => r.text());
        tm.gpuCbt = new GpuCbt(renderer.device, cbtCode);

        // Create GPU CBT state for all existing tiles
        for (const [, state] of active) {
          state.gpuCbt = tm.gpuCbt.createTileState(
            renderer.device,
            state.heightmapView,
            renderer.sampler,
          );
        }
        console.log('GPU CBT enabled');
      } catch (e) {
        console.warn('GPU CBT not available, falling back to CPU:', e);
        tm.gpuCbtEnabled = false;
        tm.gpuCbt = null;
      }
    }

    return tm;
  }

  private lod0DataForSlot(slotIdx: number): Uint8Array {
    const tileBytes = this.lod0Res * this.lod0Res * 2;
    const offset = slotIdx * tileBytes;
    return new Uint8Array(this.lod0Data, offset, tileBytes);
  }

  // --- Performance timing (ms, exponential moving average) ---
  perf = { cull: 0, cbtUpdate: 0, meshGen: 0, upload: 0, infer: 0, total: 0, cbtLeaves: 0, cbtTilesUpdated: 0 };
  private _perfAlpha = 0.05; // EMA smoothing factor

  update(
    vp: Float32Array,
    cameraPosX: number, cameraPosY: number, cameraPosZ: number,
    cameraDirX: number, cameraDirY: number, cameraDirZ: number,
    screenW: number, screenH: number,
    elevScale: number, lodThreshold: number,
  ): { visibleCount: number; totalTris: number } {
    const t0 = performance.now();
    this.frameCounter++;
    const frustum = extractFrustumPlanes(vp);

    // 1. Frustum cull — reuse typed arrays (zero allocations)
    let visCount = 0;
    const visIdx = this._visibleIdx;
    const visDist = this._visibleDist;

    for (let i = 0; i < this.slots.length; i++) {
      const slot = this.slots[i];
      const pad = 0.05;
      if (aabbVsFrustum(
        slot.offsetX - 0.5 - pad,
        slot.elevMin * 0.00001 * elevScale - pad,
        slot.offsetZ - 0.5 - pad,
        slot.offsetX + 0.5 + pad,
        slot.elevMax * 0.00001 * elevScale + pad,
        slot.offsetZ + 0.5 + pad,
        frustum,
      )) {
        const dx = slot.offsetX - cameraPosX;
        const dy = slot.offsetY - cameraPosY;
        const dz = slot.offsetZ - cameraPosZ;
        visIdx[visCount] = i;
        visDist[visCount] = Math.sqrt(dx * dx + dy * dy + dz * dz);
        visCount++;

        const state = this.active.get(i);
        if (state) state.lastVisibleFrame = this.frameCounter;
      }
    }

    // Sort by distance (insertion sort — fast for mostly-sorted small arrays)
    for (let i = 1; i < visCount; i++) {
      const tmpIdx = visIdx[i], tmpDist = visDist[i];
      let j = i - 1;
      while (j >= 0 && visDist[j] > tmpDist) {
        visIdx[j + 1] = visIdx[j];
        visDist[j + 1] = visDist[j];
        j--;
      }
      visIdx[j + 1] = tmpIdx;
      visDist[j + 1] = tmpDist;
    }
    this._visibleCount = visCount;

    const tCull = performance.now();

    // 2. Want focus — closest trained tiles (reuse array)
    const wantFocus = this._wantFocus;
    wantFocus.length = 0;
    for (let vi = 0; vi < visCount; vi++) {
      if (wantFocus.length >= MAX_LOD1_TILES) break;
      if (this.slots[visIdx[vi]].trained) wantFocus.push(visIdx[vi]);
    }

    // 3. Focus activation (max 1 per frame, async)
    if (this.gpuInference) {
      for (const slotIdx of wantFocus) {
        const state = this.active.get(slotIdx);
        if (!state || state.currentLod >= LodLevel.Lod1) continue;
        if (this.fetchingSlots.has(slotIdx)) continue;

        this.activateTile(slotIdx);
        break;
      }
    }

    // 4. Per-frame inference — dispatch + consume previous readback
    //    Targets detail patch when available, otherwise base hires texture
    const tInfer0 = performance.now();
    if (this.gpuInference) {
      for (const slotIdx of wantFocus) {
        const state = this.active.get(slotIdx);
        if (!state) continue;

        const slot = this.slots[slotIdx];
        const fovealUV = projectFovealCenter(
          cameraPosX, cameraPosY, cameraPosZ,
          cameraDirX, cameraDirY, cameraDirZ,
          slot.offsetX, slot.offsetZ,
        );

        // Detail patch sliding — move patch toward camera foveal center
        if (state.detailTexture && state.detailOccupancy) {
          this.slideDetailPatch(state, fovealUV, slotIdx);
        }

        // Choose inference target: detail patch if available, else base
        if (state.detailModel && state.detailOccupancy && state.detailTexture) {
          // Infer into detail patch
          const totalPixels = DETAIL_RES * DETAIL_RES;
          if (state.detailRefinementCount >= totalPixels) continue;

          // Foveal UV relative to detail patch
          const detailFovealU = (fovealUV[0] - state.detailOriginU) / state.detailScale;
          const detailFovealV = (fovealUV[1] - state.detailOriginV) / state.detailScale;
          const clampedFoveal: [number, number] = [
            Math.max(0, Math.min(1, detailFovealU)),
            Math.max(0, Math.min(1, detailFovealV)),
          ];

          const budget = Math.min(INFER_BUDGET, totalPixels - state.detailRefinementCount);
          const coordsArr = collectUnrefinedCoords(state.detailOccupancy, DETAIL_RES, clampedFoveal, budget);

          if (coordsArr.length > 0) {
            const consumed = this.gpuInference.dispatchAndScatter(
              this.renderer.device, this.renderer.queue,
              state.detailModel, coordsArr,
            );

            if (consumed) {
              for (let i = 0; i < consumed.length; i++) {
                const packed = consumed[i];
                const px = packed >>> 16;
                const py = packed & 0xffff;
                const bitIdx = py * DETAIL_RES + px;
                state.detailOccupancy[(bitIdx / 32) | 0] |= 1 << (bitIdx % 32);
              }
              state.detailRefinementCount += consumed.length;
            }

            this.gpuInference.flushDirtyRows(this.renderer.queue, state.detailModel, state.detailTexture);
          }
        } else if (state.model) {
          // Infer into base hires texture
          if (state.refinementCount >= HIRES_RES * HIRES_RES) continue;

          const budget = Math.min(INFER_BUDGET, HIRES_RES * HIRES_RES - state.refinementCount);
          const coordsArr = collectUnrefinedCoords(state.occupancy, HIRES_RES, fovealUV, budget);

          if (coordsArr.length > 0) {
            const consumed = this.gpuInference.dispatchAndScatter(
              this.renderer.device, this.renderer.queue,
              state.model, coordsArr,
            );

            if (consumed) {
              for (let i = 0; i < consumed.length; i++) {
                const packed = consumed[i];
                const px = packed >>> 16;
                const py = packed & 0xffff;
                const bitIdx = py * HIRES_RES + px;
                state.occupancy[(bitIdx / 32) | 0] |= 1 << (bitIdx % 32);
              }
              state.refinementCount += consumed.length;
            }

            if (state.hiresTexture) {
              this.gpuInference.flushDirtyRows(this.renderer.queue, state.model, state.hiresTexture);
            }
          }
        } else {
          continue;
        }

        break; // one tile per frame
      }
    }
    const tInfer1 = performance.now();

    // 5. Downgrade distant tiles (reuse array)
    const downgradeList = this._downgradeList;
    downgradeList.length = 0;
    for (const [slotIdx, state] of this.active) {
      if (
        state.currentLod > LodLevel.Lod0 &&
        this.frameCounter - state.lastVisibleFrame > DOWNGRADE_FRAMES &&
        this.frameCounter - state.upgradeFrame > UPGRADE_HOLD_FRAMES
      ) {
        downgradeList.push(slotIdx);
      }
    }
    for (const slotIdx of downgradeList) {
      const data = this.lod0DataForSlot(slotIdx);
      const newState = createTileState(this.renderer, data, this.lod0Res, this.slots[slotIdx]);
      if (this.gpuCbt) {
        newState.gpuCbt = this.gpuCbt.createTileState(
          this.renderer.device, newState.heightmapView, this.renderer.sampler,
        );
      }
      destroyTileState(this.active.get(slotIdx)!);
      this.active.set(slotIdx, newState);
    }

    // 6. Update CBT + regenerate mesh for visible tiles
    //    Closest tiles get priority; distant tiles reuse cached meshes.
    let totalTris = 0;
    let visibleCount = 0;
    let cbtUpdatesThisFrame = 0;
    let tCbtUpdate = 0;
    let tMeshGen = 0;
    let tUpload = 0;
    let totalLeaves = 0;
    let cbtTilesUpdated = 0;

    // GPU CBT path: encode all compute passes into a shared encoder
    const gpuCbtEncoder = (this.gpuCbtEnabled && this.gpuCbt)
      ? this.renderer.device.createCommandEncoder()
      : null;

    for (let vi = 0; vi < visCount; vi++) {
      const slotIdx = visIdx[vi];
      const state = this.active.get(slotIdx);
      if (!state) continue;

      visibleCount++;

      const slot = this.slots[slotIdx];
      const localCamX = cameraPosX - slot.offsetX;
      const localCamY = cameraPosY - slot.offsetY;
      const localCamZ = cameraPosZ - slot.offsetZ;

      if (gpuCbtEncoder && state.gpuCbt) {
        // GPU CBT path: encode compute passes, no CPU mesh work
        if (cbtUpdatesThisFrame < CBT_UPDATE_BUDGET) {
          const elevRange = Math.abs(state.elevMax - state.elevMin);
          this.gpuCbt!.encodeTileUpdate(
            gpuCbtEncoder, state.gpuCbt,
            localCamX, localCamY, localCamZ,
            screenH, elevScale, elevRange, lodThreshold,
            slot.offsetX, slot.offsetZ,
          );
          cbtUpdatesThisFrame++;
        }
        // Triangle count unknown until GPU finishes — estimate from last frame
        totalTris += 100; // placeholder; exact count would need readback
      } else {
        // CPU CBT path
        state.cbt.lodThreshold = lodThreshold;

        // Throttle: only run CBT update for closest N tiles per frame
        if (cbtUpdatesThisFrame < CBT_UPDATE_BUDGET) {
          const tc0 = performance.now();
          state.cbt.update(localCamX, localCamY, localCamZ, screenW, screenH, elevScale);
          tCbtUpdate += performance.now() - tc0;
          totalLeaves += state.cbt.leafCount();
          cbtTilesUpdated++;
          cbtUpdatesThisFrame++;
        }

        // generateMesh returns cached result if CBT didn't change
        const tm0 = performance.now();
        const { vertices, indices } = state.cbt.generateMesh(elevScale);
        tMeshGen += performance.now() - tm0;

        if (indices.length === 0) {
          state.numIndices = 0;
          continue;
        }

        // Skip world-space transform + GPU upload when mesh is unchanged
        if (vertices === state._lastVertices && indices === state._lastIndices) {
          totalTris += (state.numIndices / 3) | 0;
          continue;
        }
        state._lastVertices = vertices;
        state._lastIndices = indices;

        const tu0 = performance.now();

        // Offset vertices to world space — reuse buffer if large enough
        const numVerts = (vertices.length / 5) | 0;
        let worldVerts = state._worldVerts;
        if (!worldVerts || worldVerts.length < vertices.length) {
          worldVerts = new Float32Array(vertices.length);
          state._worldVerts = worldVerts;
        }
        for (let v = 0; v < numVerts; v++) {
          const base = v * 5;
          worldVerts[base] = vertices[base] + slot.offsetX;
          worldVerts[base + 1] = vertices[base + 1];
          worldVerts[base + 2] = vertices[base + 2] + slot.offsetZ;
          worldVerts[base + 3] = vertices[base + 3];
          worldVerts[base + 4] = vertices[base + 4];
        }

        const vertBytes = vertices.byteLength;
        const idxBytes = indices.byteLength;

        // Grow buffers if needed
        if (vertBytes > state.vertexCapacity) {
          state.vertexBuffer.destroy();
          state.vertexCapacity = nextPow2(vertBytes);
          state.vertexBuffer = this.renderer.device.createBuffer({
            size: state.vertexCapacity,
            usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
          });
        }
        if (idxBytes > state.indexCapacity) {
          state.indexBuffer.destroy();
          state.indexCapacity = nextPow2(idxBytes);
          state.indexBuffer = this.renderer.device.createBuffer({
            size: state.indexCapacity,
            usage: GPUBufferUsage.INDEX | GPUBufferUsage.COPY_DST,
          });
        }

        this.renderer.queue.writeBuffer(state.vertexBuffer, 0, worldVerts, 0, vertices.length);
        this.renderer.queue.writeBuffer(state.indexBuffer, 0, indices);
        tUpload += performance.now() - tu0;

        state.numIndices = indices.length;
        totalTris += (indices.length / 3) | 0;
      }
    }

    // Submit GPU CBT compute passes
    if (gpuCbtEncoder) {
      this.renderer.queue.submit([gpuCbtEncoder.finish()]);
    }

    // Zero out non-visible tiles after grace period — reuse bitfield
    if (this._visibleBits.length < this.slots.length) {
      this._visibleBits = new Uint8Array(this.slots.length);
    }
    const vb = this._visibleBits;
    for (let vi = 0; vi < visCount; vi++) vb[visIdx[vi]] = 1;

    for (const [slotIdx, state] of this.active) {
      if (!vb[slotIdx] && this.frameCounter - state.lastVisibleFrame > 5) {
        state.numIndices = 0;
        // Free cached mesh data for non-visible tiles to reduce memory
        state._worldVerts = undefined;
        state._lastVertices = undefined;
        state._lastIndices = undefined;
      }
    }

    // Clear bits for next frame
    for (let vi = 0; vi < visCount; vi++) vb[visIdx[vi]] = 0;

    // Update perf EMA
    const tEnd = performance.now();
    const a = this._perfAlpha;
    this.perf.cull = this.perf.cull * (1 - a) + (tCull - t0) * a;
    this.perf.cbtUpdate = this.perf.cbtUpdate * (1 - a) + tCbtUpdate * a;
    this.perf.meshGen = this.perf.meshGen * (1 - a) + tMeshGen * a;
    this.perf.upload = this.perf.upload * (1 - a) + tUpload * a;
    this.perf.infer = this.perf.infer * (1 - a) + (tInfer1 - tInfer0) * a;
    this.perf.total = this.perf.total * (1 - a) + (tEnd - t0) * a;
    this.perf.cbtLeaves = this.perf.cbtLeaves * (1 - a) + totalLeaves * a;
    this.perf.cbtTilesUpdated = this.perf.cbtTilesUpdated * (1 - a) + cbtTilesUpdated * a;

    return { visibleCount, totalTris };
  }

  /** Slide the detail patch toward the camera foveal center with hysteresis. */
  private slideDetailPatch(state: TileRenderState, fovealUV: [number, number], slotIdx: number): void {
    if (!state.detailOccupancy || !state.detailModel || !state.detailTexture) return;

    const scale = state.detailScale;
    const halfScale = scale * 0.5;

    // Ideal center = foveal UV, clamped so patch stays inside [0,1]
    const idealCenterU = Math.max(halfScale, Math.min(1 - halfScale, fovealUV[0]));
    const idealCenterV = Math.max(halfScale, Math.min(1 - halfScale, fovealUV[1]));

    const curCenterU = state.detailOriginU + halfScale;
    const curCenterV = state.detailOriginV + halfScale;

    const dU = idealCenterU - curCenterU;
    const dV = idealCenterV - curCenterV;
    const dist = Math.sqrt(dU * dU + dV * dV);

    // Only move if shifted more than 25% of scale
    if (dist < scale * 0.25) return;

    // Shift by 50% of scale toward ideal center (hysteresis)
    const shiftAmount = scale * 0.5;
    const shiftU = dU === 0 ? 0 : Math.sign(dU) * Math.min(Math.abs(dU), shiftAmount);
    const shiftV = dV === 0 ? 0 : Math.sign(dV) * Math.min(Math.abs(dV), shiftAmount);

    const newOriginU = Math.max(0, Math.min(1 - scale, state.detailOriginU + shiftU));
    const newOriginV = Math.max(0, Math.min(1 - scale, state.detailOriginV + shiftV));

    // If origin barely changed, skip
    if (Math.abs(newOriginU - state.detailOriginU) < 0.001 &&
        Math.abs(newOriginV - state.detailOriginV) < 0.001) return;

    // Shift in pixels
    const pixShiftU = Math.round((newOriginU - state.detailOriginU) / scale * DETAIL_RES);
    const pixShiftV = Math.round((newOriginV - state.detailOriginV) / scale * DETAIL_RES);

    const rowBytes = DETAIL_RES * 2;
    const oldShadow = state.detailModel.shadowData;

    // 1. Shift shadow buffer — copy overlapping pixels to new positions
    const newShadow = new Uint8Array(oldShadow.length);
    for (let dstY = 0; dstY < DETAIL_RES; dstY++) {
      const srcY = dstY + pixShiftV;
      if (srcY < 0 || srcY >= DETAIL_RES) continue;

      // Compute x range for overlap
      const srcXStart = Math.max(0, pixShiftU);
      const srcXEnd = Math.min(DETAIL_RES, DETAIL_RES + pixShiftU);
      const dstXStart = srcXStart - pixShiftU;

      if (srcXStart >= srcXEnd) continue;

      const srcOff = srcY * rowBytes + srcXStart * 2;
      const dstOff = dstY * rowBytes + dstXStart * 2;
      const copyLen = (srcXEnd - srcXStart) * 2;
      newShadow.set(oldShadow.subarray(srcOff, srcOff + copyLen), dstOff);
    }

    // 2. Fill newly exposed areas with upsampled LOD0
    const lod0Data = this.lod0DataForSlot(slotIdx);
    const lod0Fill = upsampleLod0ToDetailPatch(
      lod0Data, this.lod0Res, DETAIL_RES,
      newOriginU, newOriginV, scale,
    );
    const fillU16 = new Uint16Array(lod0Fill.buffer);
    const newU16 = new Uint16Array(newShadow.buffer);

    // Fill rows that had no source (top/bottom strips)
    for (let dstY = 0; dstY < DETAIL_RES; dstY++) {
      const srcY = dstY + pixShiftV;
      const dstRow = dstY * DETAIL_RES;

      if (srcY < 0 || srcY >= DETAIL_RES) {
        // Entire row is newly exposed
        for (let x = 0; x < DETAIL_RES; x++) {
          newU16[dstRow + x] = fillU16[dstRow + x];
        }
      } else {
        // Fill left/right exposed columns
        if (pixShiftU > 0) {
          // Left strip: dst columns [DETAIL_RES - pixShiftU, DETAIL_RES) are new
          const startX = DETAIL_RES - pixShiftU;
          for (let x = startX; x < DETAIL_RES; x++) {
            newU16[dstRow + x] = fillU16[dstRow + x];
          }
        } else if (pixShiftU < 0) {
          // Right strip: dst columns [0, -pixShiftU) are new
          const endX = -pixShiftU;
          for (let x = 0; x < endX; x++) {
            newU16[dstRow + x] = fillU16[dstRow + x];
          }
        }
      }
    }

    // 3. Remap occupancy — shift existing bits, clear newly exposed areas
    const newOccupancy = new Uint32Array(state.detailOccupancy.length);
    let newRefinementCount = 0;

    for (let py = 0; py < DETAIL_RES; py++) {
      const srcY = py + pixShiftV;
      if (srcY < 0 || srcY >= DETAIL_RES) continue;
      for (let px = 0; px < DETAIL_RES; px++) {
        const srcX = px + pixShiftU;
        if (srcX < 0 || srcX >= DETAIL_RES) continue;

        const srcBit = srcY * DETAIL_RES + srcX;
        if (state.detailOccupancy[(srcBit / 32) | 0] & (1 << (srcBit % 32))) {
          const dstBit = py * DETAIL_RES + px;
          newOccupancy[(dstBit / 32) | 0] |= 1 << (dstBit % 32);
          newRefinementCount++;
        }
      }
    }

    // 4. Mark any in-flight inference results for discard (they're for the old origin).
    //    Don't null pendingCoords — let the normal consumption path unmap the buffer
    //    to avoid orphaning mapped buffers.
    if (state.detailModel.pendingCoords) {
      state.detailModel.pendingDiscard = true;
    }

    // 5. Commit updates
    state.detailOriginU = newOriginU;
    state.detailOriginV = newOriginV;
    state.detailOccupancy = newOccupancy;
    state.detailRefinementCount = newRefinementCount;
    state.detailModel.shadowData = newShadow;

    // Update coord remap to reflect new origin
    state.detailModel.baseParams.coordOffsetU = newOriginU;
    state.detailModel.baseParams.coordOffsetV = newOriginV;

    // 5. Upload entire shifted shadow buffer to detail texture
    this.renderer.queue.writeTexture(
      { texture: state.detailTexture },
      newShadow,
      { bytesPerRow: rowBytes, rowsPerImage: DETAIL_RES },
      { width: DETAIL_RES, height: DETAIL_RES },
    );

    // Mark all rows dirty so any pending flushDirtyRows doesn't write stale data
    state.detailModel.dirtyRows.fill(0n);
  }

  private async activateTile(slotIdx: number): Promise<void> {
    if (this.fetchingSlots.has(slotIdx)) return;
    this.fetchingSlots.add(slotIdx);

    const slot = this.slots[slotIdx];
    const url = `${this.weightsBaseUrl}tile_${String(slot.tileId).padStart(4, '0')}.weights`;

    try {
      const ta0 = performance.now();
      const resp = await fetch(url);
      if (!resp.ok) {
        console.warn(`Failed to fetch weights for tile ${slot.tileId}: ${resp.status}`);
        return;
      }

      const weightsData = await resp.arrayBuffer();
      const ta1 = performance.now();

      const tileWeights = parseTileWeights(weightsData);
      const ta2 = performance.now();

      const lod0Data = this.lod0DataForSlot(slotIdx);

      const device = this.renderer.device;
      const queue = this.renderer.queue;

      // Base texture at 1024² (always)
      const hiresTexture = device.createTexture({
        size: { width: HIRES_RES, height: HIRES_RES },
        format: 'r16float',
        usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
      });

      const upsampled = upsampleLod0ToHires(lod0Data, this.lod0Res, HIRES_RES);
      const ta3 = performance.now();

      queue.writeTexture(
        { texture: hiresTexture },
        upsampled,
        { bytesPerRow: HIRES_RES * 2, rowsPerImage: HIRES_RES },
        { width: HIRES_RES, height: HIRES_RES },
      );

      const textureView = hiresTexture.createView();

      // Detail texture at DETAIL_RES (1024²), covers sub-region
      const detailTexture = device.createTexture({
        size: { width: DETAIL_RES, height: DETAIL_RES },
        format: 'r16float',
        usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
      });

      // Initial detail patch origin: centered at (0.5, 0.5)
      const detailOriginU = Math.max(0, Math.min(1 - DETAIL_SCALE, 0.5 - DETAIL_SCALE / 2));
      const detailOriginV = Math.max(0, Math.min(1 - DETAIL_SCALE, 0.5 - DETAIL_SCALE / 2));

      // Upsample LOD0 into the detail patch sub-region
      const detailUpsampled = upsampleLod0ToDetailPatch(
        lod0Data, this.lod0Res, DETAIL_RES,
        detailOriginU, detailOriginV, DETAIL_SCALE,
      );
      const ta3b = performance.now();

      queue.writeTexture(
        { texture: detailTexture },
        detailUpsampled,
        { bytesPerRow: DETAIL_RES * 2, rowsPerImage: DETAIL_RES },
        { width: DETAIL_RES, height: DETAIL_RES },
      );

      const detailView = detailTexture.createView();

      const uniformBuffer = this.renderer.createUniformBuffer();
      const bindGroup = this.renderer.createTileBindGroup(uniformBuffer, textureView, detailView);

      const cbt = new Cbt(
        lod0Data.buffer.slice(lod0Data.byteOffset, lod0Data.byteOffset + lod0Data.byteLength),
        this.lod0Res, slot.elevMin, slot.elevMax,
      );

      const occupancyWords = (HIRES_RES * HIRES_RES / 32) | 0;
      const detailOccupancyWords = (DETAIL_RES * DETAIL_RES / 32) | 0;

      const newState: TileRenderState = {
        bindGroup,
        uniformBuffer,
        heightmapTexture: hiresTexture,
        heightmapView: textureView,
        vertexBuffer: device.createBuffer({
          size: 60,
          usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
        }),
        indexBuffer: device.createBuffer({
          size: 12,
          usage: GPUBufferUsage.INDEX | GPUBufferUsage.COPY_DST,
        }),
        numIndices: 0,
        vertexCapacity: 60,
        indexCapacity: 12,
        cbt,
        currentLod: LodLevel.Lod1,
        lastVisibleFrame: this.frameCounter,
        upgradeFrame: this.frameCounter,
        elevMin: slot.elevMin,
        elevMax: slot.elevMax,
        texelSize: 1.0 / HIRES_RES,
        hiresTexture,
        occupancy: new Uint32Array(occupancyWords),
        refinementCount: 0,
        model: undefined,
        // Detail patch fields
        detailTexture,
        detailView,
        detailOriginU,
        detailOriginV,
        detailScale: DETAIL_SCALE,
        detailOccupancy: new Uint32Array(detailOccupancyWords),
        detailRefinementCount: 0,
        detailModel: undefined,
      };

      const ta4 = performance.now();

      // Create base model buffers (for base texture inference)
      newState.model = this.gpuInference!.createModelBuffers(
        device, queue, tileWeights, HIRES_RES, upsampled,
      );

      // Create detail model buffers (same weights, targets detail patch)
      newState.detailModel = this.gpuInference!.createModelBuffers(
        device, queue, tileWeights, DETAIL_RES, detailUpsampled,
      );
      // Set coord remap so inference maps detail pixels to tile UV space
      newState.detailModel.baseParams.coordScale = DETAIL_SCALE;
      newState.detailModel.baseParams.coordOffsetU = detailOriginU;
      newState.detailModel.baseParams.coordOffsetV = detailOriginV;

      const ta5 = performance.now();

      // Create GPU CBT state if enabled
      if (this.gpuCbt) {
        newState.gpuCbt = this.gpuCbt.createTileState(
          device, textureView, this.renderer.sampler,
        );
      }

      // Destroy old state
      const old = this.active.get(slotIdx);
      if (old) destroyTileState(old);

      this.active.set(slotIdx, newState);
      console.log(`Activate tile ${slot.tileId}: fetch ${(ta1 - ta0).toFixed(1)} | parse ${(ta2 - ta1).toFixed(1)} | upsample ${(ta3 - ta2).toFixed(1)} | detail upsample ${(ta3b - ta3).toFixed(1)} | gpu setup ${(ta4 - ta3b).toFixed(1)} | model ${(ta5 - ta4).toFixed(1)} | total ${(ta5 - ta0).toFixed(1)} ms`);
    } catch (e) {
      console.warn(`Error activating tile ${slot.tileId}:`, e);
    } finally {
      this.fetchingSlots.delete(slotIdx);
    }
  }

  setHiresResolution(res: number): void {
    setHiresRes(res);
    // Force-downgrade all LOD1 tiles — they'll re-activate at the new resolution
    for (const [slotIdx, state] of this.active) {
      if (state.currentLod === LodLevel.Lod1) {
        const data = this.lod0DataForSlot(slotIdx);
        const newState = createTileState(this.renderer, data, this.lod0Res, this.slots[slotIdx]);
        if (this.gpuCbt) {
          newState.gpuCbt = this.gpuCbt.createTileState(
            this.renderer.device, newState.heightmapView, this.renderer.sampler,
          );
        }
        destroyTileState(state);
        this.active.set(slotIdx, newState);
      }
    }
    // Cancel in-flight activations so they restart at new resolution
    this.fetchingSlots.clear();
  }

  setDetailZoom(zoom: number): void {
    setDetailZoomGlobal(zoom);
    // Force-downgrade all LOD1 tiles — they'll re-activate with the new detail scale
    for (const [slotIdx, state] of this.active) {
      if (state.currentLod === LodLevel.Lod1) {
        const data = this.lod0DataForSlot(slotIdx);
        const newState = createTileState(this.renderer, data, this.lod0Res, this.slots[slotIdx]);
        if (this.gpuCbt) {
          newState.gpuCbt = this.gpuCbt.createTileState(
            this.renderer.device, newState.heightmapView, this.renderer.sampler,
          );
        }
        destroyTileState(state);
        this.active.set(slotIdx, newState);
      }
    }
    this.fetchingSlots.clear();
  }

  iterActive(): IterableIterator<TileRenderState> {
    return this.active.values();
  }
}

// --- Helpers ---

function createTileState(
  renderer: Renderer,
  heightmapData: Uint8Array,
  res: number,
  slot: TileSlot,
): TileRenderState {
  const device = renderer.device;
  const queue = renderer.queue;

  const texture = device.createTexture({
    size: { width: res, height: res },
    format: 'r16float',
    usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
  });
  queue.writeTexture(
    { texture },
    heightmapData,
    { bytesPerRow: res * 2, rowsPerImage: res },
    { width: res, height: res },
  );
  const textureView = texture.createView();

  const uniformBuffer = renderer.createUniformBuffer();
  const bindGroup = renderer.createTileBindGroup(uniformBuffer, textureView);

  const cbt = new Cbt(
    heightmapData.buffer.slice(heightmapData.byteOffset, heightmapData.byteOffset + heightmapData.byteLength),
    res, slot.elevMin, slot.elevMax,
  );

  return {
    bindGroup,
    uniformBuffer,
    heightmapTexture: texture,
    heightmapView: textureView,
    vertexBuffer: device.createBuffer({
      size: 60,
      usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
    }),
    indexBuffer: device.createBuffer({
      size: 12,
      usage: GPUBufferUsage.INDEX | GPUBufferUsage.COPY_DST,
    }),
    numIndices: 0,
    vertexCapacity: 60,
    indexCapacity: 12,
    cbt,
    currentLod: LodLevel.Lod0,
    lastVisibleFrame: 0,
    upgradeFrame: 0,
    elevMin: slot.elevMin,
    elevMax: slot.elevMax,
    texelSize: 1.0 / res,
    occupancy: new Uint32Array(0),
    refinementCount: 0,
    detailOriginU: 0,
    detailOriginV: 0,
    detailScale: DETAIL_SCALE,
    detailRefinementCount: 0,
  };
}

/** Safely unmap readback buffers before destroying a model's resources. */
function cleanupModelBuffers(model: TileModelBuffers): void {
  // Unmap any mapped readback buffer before destroying
  if (model.readbackReady && model.pendingCoords) {
    try {
      const prevFront = 1 - model.readbackFront;
      model.readbackBufs[prevFront].unmap();
    } catch { /* already unmapped */ }
  }
  model.pendingCoords = null;
  model.readbackReady = false;

  model.weightsBuf.destroy();
  model.freqsBuf.destroy();
  model.gridTex.destroy();
  model.outBuf.destroy();
  model.coordsBuf.destroy();
  model.paramsBuf.destroy();
  model.readbackBufs[0].destroy();
  model.readbackBufs[1].destroy();
}

function destroyTileState(state: TileRenderState): void {
  state.vertexBuffer.destroy();
  state.indexBuffer.destroy();
  state.uniformBuffer.destroy();
  state.heightmapTexture.destroy();
  // Don't destroy hiresTexture separately — for Lod1 tiles it's the same as heightmapTexture
  if (state.gpuCbt) {
    // GPU CBT buffers are owned by the tile state
    state.gpuCbt.bitfield.destroy();
    state.gpuCbt.leafList.destroy();
    state.gpuCbt.leafCount.destroy();
    state.gpuCbt.flags.destroy();
    state.gpuCbt.vertexBuf.destroy();
    state.gpuCbt.indirectBuf.destroy();
    state.gpuCbt.paramsBuffer.destroy();
  }
  if (state.model) cleanupModelBuffers(state.model);
  // Detail patch cleanup
  if (state.detailTexture) {
    state.detailTexture.destroy();
  }
  if (state.detailModel) cleanupModelBuffers(state.detailModel);
}

function projectFovealCenter(
  camX: number, camY: number, camZ: number,
  dirX: number, dirY: number, dirZ: number,
  tileOffX: number, tileOffZ: number,
): [number, number] {
  if (Math.abs(dirY) < 1e-6) return [0.5, 0.5];
  const t = -camY / dirY;
  if (t < 0) return [0.5, 0.5];

  const hitX = camX + t * dirX;
  const hitZ = camZ + t * dirZ;

  const u = Math.max(0, Math.min(1, hitX - (tileOffX - 0.5)));
  const v = Math.max(0, Math.min(1, hitZ - (tileOffZ - 0.5)));
  return [u, v];
}

// Reusable coords buffer — avoids dynamic array + Uint32Array copy per frame
let _coordsBuf = new Uint32Array(INFER_BUDGET);

function collectUnrefinedCoords(
  occupancy: Uint32Array,
  res: number,
  fovealUV: [number, number],
  budget: number,
): Uint32Array {
  if (_coordsBuf.length < budget) _coordsBuf = new Uint32Array(budget);
  const cx = Math.min(res - 1, (fovealUV[0] * res) | 0);
  const cy = Math.min(res - 1, (fovealUV[1] * res) | 0);
  const resI = res | 0;
  let count = 0;

  for (let dist = 0; dist < resI * 2; dist++) {
    if (count >= budget) break;

    if (dist === 0) {
      if (cx >= 0 && cx < resI && cy >= 0 && cy < resI) {
        const bitIdx = cy * res + cx;
        if ((occupancy[(bitIdx / 32) | 0] & (1 << (bitIdx % 32))) === 0) {
          _coordsBuf[count++] = (cx << 16) | cy;
        }
      }
      continue;
    }

    for (let side = 0; side < 4; side++) {
      for (let step = 0; step < dist; step++) {
        if (count >= budget) break;

        let px: number, py: number;
        switch (side) {
          case 0: px = cx + step; py = cy - dist + step; break;
          case 1: px = cx + dist - step; py = cy + step; break;
          case 2: px = cx - step; py = cy + dist - step; break;
          default: px = cx - dist + step; py = cy - step; break;
        }

        if (px < 0 || px >= resI || py < 0 || py >= resI) continue;

        const bitIdx = py * res + px;
        if ((occupancy[(bitIdx / 32) | 0] & (1 << (bitIdx % 32))) === 0) {
          _coordsBuf[count++] = (px << 16) | py;
        }
      }
    }
  }

  return _coordsBuf.subarray(0, count);
}

/**
 * Upsample LOD0 data into a detail patch sub-region.
 * The patch covers [originU, originU+scale] × [originV, originV+scale] in tile UV space.
 * Output is DETAIL_RES² f16 pixels.
 */
function upsampleLod0ToDetailPatch(
  lod0Data: Uint8Array,
  lod0Res: number,
  detailRes: number,
  originU: number,
  originV: number,
  scale: number,
): Uint8Array {
  // Reuse the existing bilinear upsample but with coordinate mapping
  // Detail pixel (px, py) → tile UV → LOD0 source coordinate
  const src = new Uint16Array(lod0Data.buffer, lod0Data.byteOffset, lod0Data.byteLength / 2);
  const dst = new Uint16Array(detailRes * detailRes);

  // Build f16 LUT for source decoding
  const lut = getF16LutLocal();

  // Fast f32→f16 via bit manipulation
  const _f2hBuf = new Float32Array(1);
  const _f2hU32 = new Uint32Array(_f2hBuf.buffer);
  const fastF32ToF16 = (v: number): number => {
    _f2hBuf[0] = v;
    const b = _f2hU32[0];
    const sign = (b >>> 16) & 0x8000;
    const expo = ((b >>> 23) & 0xff) - 127 + 15;
    if (expo <= 0) return sign;
    if (expo >= 31) return sign | 0x7c00;
    return sign | (expo << 10) | ((b >>> 13) & 0x3ff);
  };

  for (let py = 0; py < detailRes; py++) {
    // Detail pixel py → tile UV v → LOD0 source y
    const tileV = originV + (py + 0.5) / detailRes * scale;
    const sy = tileV * lod0Res - 0.5;
    const j0 = Math.max(0, sy | 0);
    const y0 = Math.min(j0, lod0Res - 1);
    const y1 = Math.min(j0 + 1, lod0Res - 1);
    const fy = sy - (sy | 0);
    const fy1 = 1 - fy;
    const srcRow0 = y0 * lod0Res;
    const srcRow1 = y1 * lod0Res;
    const dstRow = py * detailRes;

    for (let px = 0; px < detailRes; px++) {
      const tileU = originU + (px + 0.5) / detailRes * scale;
      const sx = tileU * lod0Res - 0.5;
      const i0 = Math.max(0, sx | 0);
      const ix0 = Math.min(i0, lod0Res - 1);
      const ix1 = Math.min(i0 + 1, lod0Res - 1);
      const fx = sx - (sx | 0);

      const val = (lut[src[srcRow0 + ix0]] * (1 - fx) + lut[src[srcRow0 + ix1]] * fx) * fy1 +
                  (lut[src[srcRow1 + ix0]] * (1 - fx) + lut[src[srcRow1 + ix1]] * fx) * fy;
      dst[dstRow + px] = fastF32ToF16(val);
    }
  }

  return new Uint8Array(dst.buffer);
}

// Local f16 LUT (shares the one from gpu-inference if already created)
let _f16LutLocal: Float32Array | null = null;
function getF16LutLocal(): Float32Array {
  if (_f16LutLocal) return _f16LutLocal;
  _f16LutLocal = new Float32Array(65536);
  for (let i = 0; i < 65536; i++) {
    const sign = (i >>> 15) & 1;
    const exp = (i >>> 10) & 0x1f;
    const frac = i & 0x3ff;
    if (exp === 0) {
      _f16LutLocal[i] = frac === 0 ? (sign ? -0 : 0) : (sign ? -1 : 1) * (frac / 1024) * Math.pow(2, -14);
    } else if (exp === 0x1f) {
      _f16LutLocal[i] = frac ? NaN : (sign ? -Infinity : Infinity);
    } else {
      _f16LutLocal[i] = (sign ? -1 : 1) * Math.pow(2, exp - 15) * (1 + frac / 1024);
    }
  }
  return _f16LutLocal;
}

function nextPow2(v: number): number {
  v--;
  v |= v >> 1; v |= v >> 2; v |= v >> 4;
  v |= v >> 8; v |= v >> 16;
  return v + 1;
}
